import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, MapPin, Phone, Mail, Clock } from "lucide-react";
import { BUSINESS } from "@/data/business";
import { TikTokIcon } from "./TikTokIcon";

const FOOTER_LINKS = [
  ["Home", "/"],
  ["About", "/about"],
  ["Services", "/services"],
  ["Gallery", "/gallery"],
  ["Contact", "/contact"],
];

const socialLinkClass =
  "inline-flex h-9 w-9 items-center justify-center rounded-full border border-[color:rgba(43,31,23,0.14)] bg-[#FFFCF6]/60 text-[color:var(--text)] transition-colors duration-300 hover:bg-[#FFFCF6]";

const SocialRow = () => (
  <div className="mt-5 flex items-center gap-3">
    <a href={BUSINESS.social.instagram} target="_blank" rel="noreferrer" aria-label="Instagram" data-testid="footer-instagram-link" className={socialLinkClass}>
      <Instagram size={17} strokeWidth={1.75} />
    </a>
    <a href={BUSINESS.social.facebook} target="_blank" rel="noreferrer" aria-label="Facebook" data-testid="footer-facebook-link" className={socialLinkClass}>
      <Facebook size={17} strokeWidth={1.75} />
    </a>
    <a href={BUSINESS.social.tiktok} target="_blank" rel="noreferrer" aria-label="TikTok" data-testid="footer-tiktok-link" className={socialLinkClass}>
      <TikTokIcon size={18} />
    </a>
  </div>
);

const BrandBlock = () => (
  <div>
    <div className="font-serif text-2xl tracking-tight">
      Bahra <span className="italic font-light">Skincare</span>
    </div>
    <p className="mt-4 max-w-xs text-sm leading-relaxed text-[color:var(--text-muted)]">
      Personalized, organic skincare for a youthful, radiant glow. A women-owned day spa, by appointment only.
    </p>
    <SocialRow />
  </div>
);

const ExploreLinks = () => (
  <div>
    <h4 className="eyebrow mb-4">Explore</h4>
    <ul className="space-y-2.5 text-sm">
      {FOOTER_LINKS.map(([label, to]) => (
        <li key={to}>
          <Link to={to} className="text-[color:var(--text-muted)] transition-colors duration-300 hover:text-[color:var(--text)]">
            {label}
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

const VisitBlock = () => (
  <div>
    <h4 className="eyebrow mb-4">Visit &amp; Contact</h4>
    <ul className="space-y-3 text-sm text-[color:var(--text-muted)]">
      <li className="flex items-start gap-2.5">
        <MapPin size={16} strokeWidth={1.75} className="mt-0.5 shrink-0" />
        <span>{BUSINESS.address}</span>
      </li>
      <li className="flex items-center gap-2.5">
        <Phone size={16} strokeWidth={1.75} className="shrink-0" />
        <a href={BUSINESS.phoneHref} className="transition-colors duration-300 hover:text-[color:var(--text)]">{BUSINESS.phone}</a>
      </li>
      <li className="flex items-center gap-2.5">
        <Mail size={16} strokeWidth={1.75} className="shrink-0" />
        <a href={BUSINESS.emailHref} className="transition-colors duration-300 hover:text-[color:var(--text)]">{BUSINESS.email}</a>
      </li>
      <li className="flex items-start gap-2.5">
        <Clock size={16} strokeWidth={1.75} className="mt-0.5 shrink-0" />
        <span>Tue–Sat 10–6, Sun 10–4 · Mon closed</span>
      </li>
    </ul>
  </div>
);

const FooterBottom = () => (
  <div className="border-t border-[color:rgba(43,31,23,0.1)]">
    <div className="container-x flex flex-col items-center justify-between gap-2 py-6 text-xs text-[color:var(--text-subtle)] sm:flex-row">
      <p>© {new Date().getFullYear()} Bahra Skincare. All rights reserved.</p>
      <p>Niles, Illinois · Licensed Skincare Specialist</p>
    </div>
  </div>
);

export const Footer = () => (
  <footer
    data-testid="site-footer"
    className="relative mt-8 border-t border-[color:rgba(43,31,23,0.1)] bg-[color:rgba(217,183,122,0.14)]"
  >
    <div className="container-x grid grid-cols-1 gap-10 py-14 md:grid-cols-3 md:gap-8">
      <BrandBlock />
      <ExploreLinks />
      <VisitBlock />
    </div>
    <FooterBottom />
  </footer>
);
